import { EMAIL_MESSAGE, REQUIRED_MESSAGE, requiredNumber } from '@wyz/utils';
import { z } from 'zod';
import { QUOTE_DISPLAY_TYPE } from '~/common/schemas';
import { CIVILITY, RIM_TYPE_ENUM } from '~/purchase/schemas';
import { IQuoteSetting } from '~/purchase/types';

// todo: this is to dynamically set nonempty when custom config will be available
function getStringType({
  required,
  email = false,
}: {
  required: boolean;
  email?: boolean;
}) {
  const type = email
    ? z.email({
        error: EMAIL_MESSAGE,
      })
    : z.string();
  if (email) {
    return required ? type : z.union([type, z.literal('')]);
  }
  return required ? type.nonempty({ message: REQUIRED_MESSAGE }) : type;
}

export const contactSchema = z.object({
  registration: getStringType({ required: false }),
  firstName: getStringType({ required: false }),
  lastName: getStringType({ required: false }),
  phone: getStringType({ required: false }),
  email: getStringType({ required: false, email: true }),
  company: getStringType({ required: false }),
  address: getStringType({ required: false }),
  zipCode: getStringType({ required: false }),
  city: getStringType({ required: false }),
  gdprAccepted: z.boolean(),
  civility: z.enum([CIVILITY.MR, CIVILITY.MRS]).nullish(),
});

export const quoteCartSchema = z.object({
  products: z.record(
    z.string(),
    z.object({
      quantity: z
        .number({
          error: (issue) =>
            issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
        })
        .min(1, REQUIRED_MESSAGE),
    }),
  ),
  contact: contactSchema,
  validityDays: z.number(),
  comment: z.string(),
  rimType: z.enum([RIM_TYPE_ENUM.STEEL, RIM_TYPE_ENUM.ALLOY]).nullish(),
});

export const generateQuoteSettingSchema = (setting?: IQuoteSetting) => {
  return z.object({
    products: z.record(
      z.string(),
      z.object({
        quantity: z
          .number({
            error: (issue) =>
              issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
          })
          .min(1, REQUIRED_MESSAGE),
      }),
    ),
    contact: z.object({
      registration: getStringType({
        required: setting?.vehicleRegistrationRequired ?? false,
      }),
      firstName: getStringType({
        required: setting?.fullNameRequired ?? false,
      }),
      lastName: getStringType({ required: setting?.fullNameRequired ?? false }),
      phone: getStringType({ required: setting?.phoneNumberRequired ?? false }),
      email: getStringType({
        required: setting?.emailRequired ?? false,
        email: true,
      }),
      company: getStringType({ required: false }),
      address: getStringType({ required: false }),
      zipCode: getStringType({ required: false }),
      city: getStringType({ required: false }),
      gdprAccepted: z.boolean(),
      civility: z.enum([CIVILITY.MR, CIVILITY.MRS]).nullish(),
    }),
    validityDays: z.number(),
    comment: z.string(),
    rimType: z.enum([RIM_TYPE_ENUM.STEEL, RIM_TYPE_ENUM.ALLOY]).nullish(),
  });
};

const basePriceEditFormSchema = z.object({
  quantity: requiredNumber,
  discountedPriceTaxIncl: requiredNumber,
  discount: z.number().nullish(),
  margin: requiredNumber,
});

export const servicePriceEditFormSchema = z.object({
  item: basePriceEditFormSchema,
});

export const itemPriceEditFormSchema = z.object({
  items: z.record(z.string(), basePriceEditFormSchema),
  mountingPrice: z.number().nullish(),
});

export const quoteSettingsFormSchema = z.object({
  showBfDiscount: z.boolean(),
  displayType: z.enum(QUOTE_DISPLAY_TYPE),
  validityDays: z.number().min(1, REQUIRED_MESSAGE).max(60, REQUIRED_MESSAGE),
  additionalMessage: z
    .string()
    .max(256, 'app.common.assert.err_value_too_long'),
  emailRequired: z.boolean(),
  fullNameRequired: z.boolean(),
  phoneNumberRequired: z.boolean(),
  vehicleRegistrationRequired: z.boolean(),
  gdpr1: z.string().max(1000, 'app.common.assert.err_value_too_long'),
  termsOfUse: z.string().max(1024, 'app.common.assert.err_value_too_long'),
  segments: z
    .array(
      z.object({
        publicKey: z.string(),
        name: z.string(),
        id: z.string(),
      }),
    )
    .max(4, 'app.common.assert.err_array_max_elements'),
  defaultSeasonId: z.number(),
});
